import {Image} from '@jobseeker/lotus/components/Image';
import {Box, Button, IconButton, Typography} from '@mui/material';
import {Tooltip} from '@jobseeker/lotus/elements';
import {HiristSaveJob} from '@jobseeker/lotus/assets/icons';
import {Loader} from '@jobseeker/lotus/components';
import {useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';

type ApplyBtnsProps = {
  handleApply: () => void;
  saveJobForLogged: () => void;
  openInsightsDialog: () => void;
  handleJobFollowup: (jobId: string) => void;
  showLoader?: boolean;
  setShowLoader?: React.Dispatch<React.SetStateAction<boolean>>;
  setApplyBtnClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  jobId: string;
  jobSaved?: boolean;
  jobApplied?: boolean;
  jobFollowedUp?: boolean;
  showApply?: boolean;
};

export const ApplyButton = ({
  handleApply,
  showLoader,
  setShowLoader,
  jobApplied = false,
  setApplyBtnClicked,
}: Partial<ApplyBtnsProps>) => {
  const profile = useSelector(
    (state: AppState) => state.user.profile.profileUpdateCohorts
  );
  return (
    <Button
      variant="contained"
      disableRipple
      sx={{
        fontSize: 16,
        mr: 1.25,
        fontWeight: 700,
        width: {xxs: '100%', sm: 91},
        ':hover': {bgcolor: '#E9630C'},
        border: 'none',
        color: 'white !important',
        ':disabled': {
          bgcolor: '#E9630C',
        },
      }}
      onClick={() => {
        setApplyBtnClicked?.(true);
        if (!Object.values(profile || {}).some(value => value === true)) {
          setShowLoader && setShowLoader(true);
        }
        handleApply?.();
      }}
      disabled={jobApplied || showLoader}
    >
      {jobApplied ? (
        'Applied'
      ) : showLoader ? (
        <Loader color="inherit" size={28} />
      ) : (
        'Apply'
      )}
    </Button>
  );
};

export const InsightsButton = ({
  openInsightsDialog,
}: Partial<ApplyBtnsProps>) => {
  return (
    <Button
      disableRipple
      variant="outlined"
      sx={{
        fontSize: 16,
        mr: 1.25,
        fontWeight: 700,
        border: '1px solid #E9630C',
        ':hover': {bgcolor: '#E9630C', color: 'white'},
      }}
      onClick={() => {
        openInsightsDialog?.();
      }}
    >
      Insights
    </Button>
  );
};

export const FollowUpButton = ({
  jobApplied,
  jobId = '0',
  jobFollowedUp,
  handleJobFollowup,
}: Partial<ApplyBtnsProps>) => {
  return (
    <Tooltip
      placement="bottom-start"
      arrow
      title={
        !jobApplied && (
          <Typography
            variant="subtitle3"
            lineHeight="16px"
            sx={{
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
          >
            You need to apply to this job before following up.
          </Typography>
        )
      }
    >
      <Button
        disableRipple
        variant="outlined"
        sx={{
          fontSize: 16,
          fontWeight: 700,
          border: '1px solid #E9630C',
          ':hover': {bgcolor: '#E9630C !important', color: 'white'},
        }}
        onClick={() => {
          handleJobFollowup?.(jobId);
        }}
        // disabled={jobFollowedUp || !jobApplied}
      >
        {jobFollowedUp ? 'Followed-up' : 'Follow-up'}
      </Button>
    </Tooltip>
  );
};

export const SaveJobButton = ({
  saveJobForLogged,
  jobSaved,
}: Partial<ApplyBtnsProps>) => {
  return (
    <Tooltip
      placement="bottom-end"
      arrow
      title={
        <Typography
          variant="subtitle3"
          lineHeight="16px"
          sx={{
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
          }}
        >
          Save this job for future reference
        </Typography>
      }
    >
      <IconButton
        disableRipple
        sx={{
          border: '1px solid #E9630C',
          borderRadius: '4px',
          bgcolor: jobSaved ? '#E9630C' : 'transparent',
          ':hover': {bgcolor: jobSaved ? '#E9630C' : 'transparent'},
        }}
        onClick={() => {
          saveJobForLogged?.();
        }}
      >
        <Image src={HiristSaveJob} height={22} width={25} alt="save-job" />
      </IconButton>
    </Tooltip>
  );
};

const Buttons = ({
  handleApply,
  saveJobForLogged,
  openInsightsDialog,
  handleJobFollowup,
  showLoader,
  setShowLoader,
  jobId = '0',
  jobSaved = false,
  jobApplied = false,
  jobFollowedUp = false,
  showApply = false,
  setApplyBtnClicked,
}: ApplyBtnsProps) => (
  <Box mt={3} display="flex" justifyContent="space-between">
    <Box>
      {showApply && (
        <ApplyButton
          handleApply={handleApply}
          showLoader={showLoader}
          setShowLoader={setShowLoader}
          jobApplied={jobApplied}
          setApplyBtnClicked={setApplyBtnClicked}
        />
      )}
      <InsightsButton openInsightsDialog={openInsightsDialog} />
      <FollowUpButton
        jobFollowedUp={jobFollowedUp}
        jobApplied={jobApplied}
        handleJobFollowup={handleJobFollowup}
        jobId={jobId}
      />
    </Box>
    <SaveJobButton saveJobForLogged={saveJobForLogged} jobSaved={jobSaved} />
  </Box>
);

export default Buttons;
